<template>
  <div>
    <p class="text-2xl mb-4">
      Homologação RCS
    </p>

    <v-card outlined>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="rows"
          :loading="loading"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
            'items-per-page-text': 'Itens por página:',
          }"
          class="elevation-0"
          no-data-text="Nenhuma solicitação encontrada"
          loading-text="Carregando dados..."
          item-key="id"
        >
          <template #item.actions="{ item }">
            <v-btn
              icon
              small
              color="info"
              title="Ver detalhes"
              @click="openDetail(item)"
            >
              <v-icon small>{{ icons.mdiEye }}</v-icon>
            </v-btn>
          </template>

          <template #item.createdAt="{ item }">
            <div class="d-flex flex-column">
              <span class="font-weight-medium">{{ formatDate(item.createdAt) }}</span>
              <span class="text-xs text--secondary">{{ formatTime(item.createdAt) }}</span>
            </div>
          </template>

          <template #item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>
              {{ statusText(item.status) }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="detailOpen"
      max-width="980"
      scrollable
    >
      <v-card v-if="selected">
        <v-card-title class="py-3">
          <div>
            <div class="text-h6 mb-1">
              Solicitação #{{ selected.id }}
            </div>
            <div class="text-caption text--secondary font-weight-regular">
              {{ selected.partnerName || selected.brand || '—' }}
              <span v-if="selected.createdAt"> · {{ formatDate(selected.createdAt) }} {{ formatTime(selected.createdAt) }}</span>
            </div>
          </div>
          <v-spacer />
          <v-chip small :color="statusColor(selected.status)" dark>
            {{ statusText(selected.status) }}
          </v-chip>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Cliente LigData</div>
                <div
                  v-for="field in clientFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Parceiro e marca</div>
                <div
                  v-for="field in brandFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card
                v-if="selected.nonConversational"
                outlined
                class="pa-3 mb-3 detail-section"
              >
                <div class="section-title">Não Conversacional</div>
                <div
                  v-for="field in nonConvFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card
                v-if="selected.conversational"
                outlined
                class="pa-3 mb-3 detail-section"
              >
                <div class="section-title">Conversacional</div>
                <div
                  v-for="field in convFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Dados do agente</div>
                <div
                  v-for="field in agentFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Contato do agente</div>
                <div
                  v-for="field in contactFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Privacidade e termos</div>
                <div
                  v-for="field in privacyFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value field-value--wrap">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>

              <v-card outlined class="pa-3 mb-3 detail-section">
                <div class="section-title">Responsável</div>
                <div
                  v-for="field in responsibleFields"
                  :key="field.label"
                  class="field-row"
                >
                  <div class="field-content">
                    <div class="field-label">{{ field.label }}</div>
                    <div class="field-value">{{ display(field.value) }}</div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    :disabled="!field.value"
                    @click="copy(field.value)"
                  >
                    <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="pa-3 detail-section">
            <div class="section-title">Arquivos</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <div class="file-box">
                  <div class="field-label mb-2">Logo</div>
                  <template v-if="selected.idfileLogo">
                    <v-avatar size="72" class="mb-2">
                      <img
                        v-if="preview.logo"
                        :src="preview.logo"
                        alt="logo"
                      >
                      <span v-else-if="preview.loading" class="text-caption">…</span>
                      <span v-else class="text-caption text--secondary">sem preview</span>
                    </v-avatar>
                    <div>
                      <v-btn
                        small
                        text
                        color="primary"
                        :href="fileUrl(selected.idfileLogo)"
                        target="_blank"
                      >
                        Abrir
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        @click="copy(fileUrl(selected.idfileLogo))"
                      >
                        <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <span v-else class="text--secondary">—</span>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="file-box">
                  <div class="field-label mb-2">Banner</div>
                  <template v-if="selected.idfileBanner">
                    <div class="banner-preview mb-2">
                      <img
                        v-if="preview.banner"
                        :src="preview.banner"
                        alt="banner"
                      >
                      <div v-else-if="preview.loading" class="banner-fallback">
                        Carregando preview…
                      </div>
                      <div v-else class="banner-fallback">
                        Preview indisponível — use Abrir
                      </div>
                    </div>
                    <div>
                      <v-btn
                        small
                        text
                        color="primary"
                        :href="fileUrl(selected.idfileBanner)"
                        target="_blank"
                      >
                        Abrir
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        @click="copy(fileUrl(selected.idfileBanner))"
                      >
                        <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                      </v-btn>
                    </div>
                  </template>
                  <span v-else class="text--secondary">—</span>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="file-box">
                  <div class="field-label mb-2">Cartão CNPJ</div>
                  <template v-if="selected.idfileCnpjCard">
                    <v-btn
                      small
                      color="primary"
                      outlined
                      :href="fileUrl(selected.idfileCnpjCard)"
                      target="_blank"
                    >
                      Abrir PDF
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      class="ml-1"
                      @click="copy(fileUrl(selected.idfileCnpjCard))"
                    >
                      <v-icon x-small>{{ icons.mdiContentCopy }}</v-icon>
                    </v-btn>
                  </template>
                  <span v-else class="text--secondary">—</span>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="detailOpen = false">
            Fechar
          </v-btn>
          <v-btn
            v-if="canDecide(selected)"
            color="error"
            outlined
            @click="refuseDialog = true"
          >
            Recusar
          </v-btn>
          <v-btn
            v-if="canDecide(selected)"
            color="success"
            @click="approveSelected"
          >
            Aprovar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="refuseDialog"
      max-width="480"
    >
      <v-card>
        <v-card-title>Recusar solicitação</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="refuseReason"
            outlined
            dense
            rows="3"
            label="Motivo da recusa"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="refuseDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :disabled="!refuseReason || refuseReason.trim().length < 3"
            @click="refuseSelected"
          >
            Confirmar recusa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { mdiEye, mdiContentCopy } from '@mdi/js'

export default {
  data() {
    return {
      loading: false,
      rows: [],
      detailOpen: false,
      selected: null,
      refuseDialog: false,
      refuseReason: '',
      preview: {
        loading: false,
        logo: null,
        banner: null,
      },
      icons: { mdiEye, mdiContentCopy },
      headers: [
        { text: '', value: 'actions', sortable: false, width: 56 },
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Data', value: 'createdAt' },
        { text: 'Cliente', value: 'userName' },
        { text: 'Parceiro', value: 'partnerName' },
        { text: 'Marca', value: 'brand' },
        { text: 'CNPJ', value: 'cnpj' },
        { text: 'Status', value: 'status' },
      ],
    }
  },
  watch: {
    detailOpen(open) {
      if (!open) this.clearPreviews()
    },
  },
  computed: {
    clientFields() {
      if (!this.selected) return []
      return [
        { label: 'Usuário', value: this.selected.userName },
        { label: 'E-mail do usuário', value: this.selected.userEmail },
      ]
    },
    brandFields() {
      if (!this.selected) return []
      return [
        { label: 'Parceiro', value: this.selected.partnerName },
        { label: 'Marca', value: this.selected.brand },
        { label: 'CNPJ', value: this.selected.cnpj },
        { label: 'Site da marca', value: this.selected.brandSite },
        { label: 'Categoria RCS', value: this.categoriesLabel(this.selected) },
      ]
    },
    nonConvFields() {
      if (!this.selected) return []
      return [
        { label: 'Nome do agente', value: this.selected.nonConversationalAgentName },
        { label: 'Código', value: this.selected.nonConversationalCode },
        { label: 'Caso de uso', value: this.selected.nonConversationalUseCase },
      ]
    },
    convFields() {
      if (!this.selected) return []
      return [
        { label: 'Nome do agente', value: this.selected.conversationalAgentName },
        { label: 'Código', value: this.selected.conversationalCode },
        { label: 'Caso de uso', value: this.selected.conversationalUseCase },
      ]
    },
    agentFields() {
      if (!this.selected) return []
      return [
        { label: 'Nome da empresa', value: this.selected.companyName },
        { label: 'Descrição', value: this.selected.description },
        { label: 'Cor', value: this.selected.color },
        { label: 'Mensagem predefinida', value: this.selected.presetMessage },
        { label: 'Canais alternativos', value: this.selected.alternativeChannels },
      ]
    },
    contactFields() {
      if (!this.selected) return []
      return [
        { label: 'DDI', value: this.selected.ddi },
        { label: 'Telefone', value: this.selected.phone },
        { label: 'E-mail', value: this.selected.agentEmail },
        { label: 'Site oficial', value: this.selected.officialSite },
      ]
    },
    privacyFields() {
      if (!this.selected) return []
      return [
        { label: 'Política de privacidade', value: this.selected.privacyUrl },
        { label: 'Termos de serviço', value: this.selected.termsUrl },
        { label: 'Opt-in', value: this.selected.optin },
        { label: 'Descrição do opt-in', value: this.selected.optinDescription },
      ]
    },
    responsibleFields() {
      if (!this.selected) return []
      return [
        { label: 'Nome', value: this.selected.responsibleName },
        { label: 'E-mail', value: this.selected.responsibleEmail },
        { label: 'Cargo', value: this.selected.responsibleRole },
      ]
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    ...mapActions('rcsHomologation', {
      fetchAll: 'findAll',
      approveRequest: 'approve',
      refuseRequest: 'refuse',
    }),
    ...mapActions('controlFile', {
      getFileById: 'findById',
    }),
    async load() {
      this.loading = true
      this.rows = await this.fetchAll({ allUsers: true })
      this.loading = false
    },
    openDetail(item) {
      this.selected = { ...item }
      this.detailOpen = true
      this.loadPreviews(item)
    },
    clearPreviews() {
      if (this.preview.logo) URL.revokeObjectURL(this.preview.logo)
      if (this.preview.banner) URL.revokeObjectURL(this.preview.banner)
      this.preview = { loading: false, logo: null, banner: null }
    },
    async loadFilePreview(fileId) {
      if (!fileId) return null
      try {
        const file = await this.getFileById(fileId)
        if (!file || !file.data) return null
        const contentType = file.contentType
          || this.contentTypeFromName(file.fileName || file.name)
          || 'image/jpeg'
        if (!String(contentType).startsWith('image/')) return null
        const blob = new Blob([new Uint8Array(file.data)], { type: contentType })
        return URL.createObjectURL(blob)
      } catch (err) {
        console.error('Erro ao carregar preview do arquivo', fileId, err)
        return null
      }
    },
    contentTypeFromName(name) {
      const ext = String(name || '').split('.').pop()?.toLowerCase()
      const map = {
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        jfif: 'image/jpeg',
        gif: 'image/gif',
        webp: 'image/webp',
        bmp: 'image/bmp',
        svg: 'image/svg+xml',
      }
      return map[ext] || null
    },
    async loadPreviews(item) {
      this.clearPreviews()
      this.preview.loading = true
      const [logo, banner] = await Promise.all([
        this.loadFilePreview(item.idfileLogo),
        this.loadFilePreview(item.idfileBanner),
      ])
      this.preview = { loading: false, logo, banner }
    },
    display(value) {
      if (value === null || value === undefined || value === '') return '—'
      return String(value)
    },
    copy(value) {
      const text = value == null || value === '' ? '' : String(value)
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$notify('Copiado para a área de transferência', 'success')
      }).catch(() => {
        this.$notify('Não foi possível copiar', 'error')
      })
    },
    canDecide(item) {
      return item && (Number(item.status) === 1 || Number(item.status) === 2)
    },
    statusText(status) {
      const map = {
        1: 'Pendente',
        2: 'Em análise',
        3: 'Aprovado',
        4: 'Recusado',
      }
      return map[Number(status)] || 'Pendente'
    },
    statusColor(status) {
      const map = {
        1: 'orange',
        2: 'blue',
        3: 'green',
        4: 'red',
      }
      return map[Number(status)] || 'grey'
    },
    categoriesLabel(item) {
      const parts = []
      if (item.nonConversational) parts.push('NON_CONVERSATIONAL')
      if (item.conversational) parts.push('CONVERSATIONAL')
      return parts.join(', ')
    },
    formatDate(value) {
      if (!value) return '—'
      return this.$day(value).format('DD/MM/YYYY')
    },
    formatTime(value) {
      if (!value) return ''
      return this.$day(value).format('HH:mm')
    },
    fileUrl(fileId) {
      if (!fileId) return ''
      return `${this.$axios.defaults.baseURL}/public/files/${fileId}`
    },
    async approveSelected() {
      const result = await this.approveRequest(this.selected.id)
      if (result === true) {
        this.$notify('Solicitação aprovada', 'success')
        this.detailOpen = false
        await this.load()
      } else {
        this.$notify(result?.response?.data?.message || 'Erro ao aprovar', 'error')
      }
    },
    async refuseSelected() {
      const result = await this.refuseRequest({
        id: this.selected.id,
        refusedReason: this.refuseReason.trim(),
      })
      if (result === true) {
        this.$notify('Solicitação recusada', 'success')
        this.refuseDialog = false
        this.refuseReason = ''
        this.detailOpen = false
        await this.load()
      } else {
        this.$notify(result?.response?.data?.message || 'Erro ao recusar', 'error')
      }
    },
  },
}
</script>

<style scoped>
.detail-section {
  background: rgba(128, 128, 128, 0.08);
}

.section-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.75;
  margin-bottom: 10px;
}

.field-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(128, 128, 128, 0.18);
}

.field-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.field-content {
  flex: 1;
  min-width: 0;
}

.field-label {
  font-size: 11px;
  opacity: 0.65;
  margin-bottom: 2px;
}

.field-value {
  font-size: 14px;
  font-weight: 500;
  word-break: break-word;
  line-height: 1.35;
}

.field-value--wrap {
  white-space: pre-wrap;
}

.file-box {
  min-height: 120px;
  padding: 8px;
  border-radius: 8px;
  background: rgba(128, 128, 128, 0.06);
}

.banner-preview {
  width: 100%;
  min-height: 72px;
  max-height: 96px;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(128, 128, 128, 0.12);
}

.banner-preview img {
  display: block;
  width: 100%;
  height: 96px;
  object-fit: cover;
}

.banner-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  padding: 8px;
  font-size: 12px;
  opacity: 0.7;
  text-align: center;
}
</style>
